import React, { DragEvent, useRef, useState, useEffect } from "react";
import { Box, Button, CircularProgress, Paper, TextField, Typography, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchResultGrid from "../../searchResultGrid/searchResultGrid";
import getDocumentTemplateColumnDef from "../../../colDef/documentTemplateColumnDef";
import DocumentTemplate from "../../../interfaces/documentTemplate";
import {
  fetchDocumentTemplatesApi,
  isValidDocx,
  uploadDocumentTemplateApi,
} from "../../../services/manageTemplatesService";
import useIsMounted from "../../../utils/useMountedRef";

const TEMPLATE_TYPES = {
  NOTICE: "Notice",
  AGENT_AUTHORIZATION_DOCUMENT: "AgentAuthorizationDocument",
} as const;

type TemplateType = typeof TEMPLATE_TYPES[keyof typeof TEMPLATE_TYPES];

const ManageDocumentTemplate: React.FC = () => {
  const [templateName, setTemplateName] = useState<string>("");
  const [templateType, setTemplateType] = useState<TemplateType>(TEMPLATE_TYPES.NOTICE);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [documentTemplates, setDocumentTemplates] = useState<DocumentTemplate[]>([]);
  const isMountedRef = useIsMounted();

  const handleFileSelection = (file: File | null) => {
    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!isValidDocx(file)) {
      alert("Only .docx files are supported");
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelection(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    handleFileSelection(f);
  };

  const fetchDocumentTemplates = async () => {
    try {
      const data = await fetchDocumentTemplatesApi();
      if (!isMountedRef.current) return;
      setDocumentTemplates(data);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error(error);
      alert("Error fetching document templates");
    }
  };

  const uploadDocumentTemplate = async () => {
    if (!templateName || !selectedFile || !templateType) return;
    setIsUploading(true);
    try {
      const { response, parsed } = await uploadDocumentTemplateApi(templateName, selectedFile, templateType);
      if (response.status === 200) {
        const successMsg = (parsed as any)?.message || "Template uploaded successfully";
        alert(successMsg);
        if (!isMountedRef.current) return;
        setTemplateName("");
        setTemplateType(TEMPLATE_TYPES.NOTICE);
        setSelectedFile(null);
        fetchDocumentTemplates();
      } else {
        console.error("Upload failed", response.status, parsed);
        alert((parsed as any)?.message || `Upload failed with status ${response.status}`);
      }
    } catch (err: any) {
      console.error("Upload error", err);
      const resp = err?.response;
      if (resp?.data?.message) {
        alert(resp.data.message);
      } else if (err?.request) {
        alert("No response from server. Check your network or try again.");
      } else {
        alert(err?.message || "An unexpected error occurred.");
      }
    } finally {
      if (isMountedRef.current) {
        setIsUploading(false);
      }
    }
  };

  useEffect(() => {
    fetchDocumentTemplates();
  }, []);

  return (
    <Box sx={{ ml: 2, mr: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1} mb={2}>
        <Typography variant="h6" component="h2">
          Manage Document Templates
        </Typography>
      </Box>

      <Paper sx={{ p: 2 }} elevation={2}>
        <Typography variant="subtitle1" gutterBottom>
          Upload New Template
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Template name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Enter Template Name"
            fullWidth
            required
            slotProps={{
              htmlInput: {
                maxLength: 200,
              },
            }}
          />

          <FormControl fullWidth required>
            <InputLabel>Template Type</InputLabel>
            <Select
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value as TemplateType)}
              label="Template Type"
            >
              <MenuItem value={TEMPLATE_TYPES.NOTICE}>Notice</MenuItem>
              <MenuItem value={TEMPLATE_TYPES.AGENT_AUTHORIZATION_DOCUMENT}>Agent Authorization Document</MenuItem>
            </Select>
          </FormControl>

          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            style={{ display: "none" }}
            onChange={handleFileInputChange}
          />

          <Paper
            onDrop={onDrop}
            onDragOver={(e: DragEvent) => e.preventDefault()}
            variant="outlined"
            sx={{ p: 2, textAlign: "center", cursor: "pointer" }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUploadIcon sx={{ fontSize: 40 }} />
            <Typography>
              {selectedFile ? selectedFile.name : "Click or drag & drop a .docx file here"}
            </Typography>
            {selectedFile && (
              <Typography variant="caption">{(selectedFile.size / 1024).toFixed(1)} KB</Typography>
            )}
          </Paper>

          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={uploadDocumentTemplate}
              disabled={!templateName || !selectedFile || !templateType || (selectedFile && !isValidDocx(selectedFile))}
            >
              {isUploading ? <CircularProgress size={24} /> : "Upload"}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box px={2} py={2} mt={1}>
        <Typography variant="h6" component="h2">
          Templates
        </Typography>
      </Box>

      <SearchResultGrid
        results={documentTemplates}
        getRowId={(r) => r.id}
        onSelectRow={() => {}}
        columns={getDocumentTemplateColumnDef()}
        height="65vh"
      />
    </Box>
  );
};

export default ManageDocumentTemplate;


